// Angular imports
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { plainToClass, classToPlain } from 'class-transformer';

// RxJs imports
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// cc-common imports
import { Utils } from 'cc-common';

// Constants imports
import { URLConstants } from '../../_constants/url-constants';
import { CacheDuration } from '../../_constants/cache-duration';

// Service imports
import { MPCacheRegistrationService } from '../../_services/shared-services/mp-cache-registration-service';

// Model imports
import { OpenProjectTaskParameterDto } from '../_models/open-project-task-parameter-dto';
import { ClosedProjectTaskParameterDto } from '../_models/closed-project-task-parameter-dto';
import { FileInfoDisplayResultDto } from '../../_models/file-info-display-result-dto';
import { ProjectTaskHistoryResultDto } from '../_models/project-task-history-result-dto';
import { ProjectTaskEditorResultDto } from '../_models/project-task-editor-result-dto';
import { ProjectTaskEditorParameterDto } from '../_models/project-task-editor-parameter-dto';
import { EmailReminderParameterDto } from '../_models/email-reminders-parameter-dto';
import { EmailReminderResultDto } from '../_models/email-reminder-result-dto';
import { ProjectTaskLinkTaskParametersDto } from '../_models/project-task-link-task-parameters-dto';
import { EmailLinkResultDto } from '../_models/email-link-result-dto';
import { UnlinkEmailParametersDto } from '../_models/unlink-email-parameters-dto';
import { LinkEmailsParametersDto } from '../_models/link-emails-parameters-dto';
import { UnlinkInternalTasksParametersDto } from '../_models/unlink-internal-tasks-parameters-dto';
import { ProjectTaskAttachmentParameterDto } from '../_models/project-task-attachment-parameter-dto';
import { ProjectTaskUpdateStatusParameterDto } from '../_models/project-task-update-status-parameter-dto';
import { ProjectTaskResponsibleUsersResultDto } from '../_models/project-task-responsible-users-result-dto';
import { ProjectTaskNotificationSettingsRawDto } from '../_models/project-task-notification-settings-raw-dto';
import { ProjectTaskReminderEmailParameterDto } from '../_models/project-task-reminder-email-parameter-dto';
import { ProjectTaskManageAttachmentsParametersDto } from '../_models/project-task-manage-attachments-parameters-dto';
import { AddProjectTaskParameterDto } from '../_models/add-project-task-parameter-dto';
import { UpdateProjectTaskParameterDto } from '../_models/update-project-task-parameter-dto';
import { LinkInternalTaskEditorResultDto } from '../_models/link-internal-task-editor-result-dto';
import { WorkOrderLinkResultDto } from '../_models/work-order-link-result-dto';
import { OpenLinkableWorkOrderParameterDto } from '../_models/open-linkable-work-orders-parameter-dto';
import { ClosedLinkableWorkOrderParameterDto } from '../_models/closed-linkable-work-order-parameter-dto';
import { ProjectTaskManageWorkOrderParameterDto } from '../_models/project-task-manage-work-order-parameter-dto';
import { ProjectTaskViewerParameterDto } from '../_models/project-task-viewer-parameter-dto';
import { BaseSaveProcessNotesParametersDto } from '../../management-common/_models/notes/base-save-process-notes-parameters-dto';
import { BaseProcessNotesResultDto } from '../../management-common/_models/notes/base-process-notes-result-dto';
import { ProjectTaskParameterDto } from '../_models/project-task-parameter-dto';
import { ProjectTaskResultDto } from '../_models/project-task-result-dto';
import { ProjectTaskItemResultDto } from '../_models/project-task-item-result-dto';
import { EmailReminderInfo } from '../../shared-module/_models/email-reminders-info';
import { EmailReminderItemInfo } from '../../shared-module/_models/email-reminder-task-info';

@Injectable({
    providedIn: 'root'
})
export class ProjectTaskService {
    private _emailRemindersSource: Array<EmailReminderItemInfo> = [];

    constructor(
        private readonly httpClient: HttpClient,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {}

    getOpenProjectTasks(param: OpenProjectTaskParameterDto): Observable<ProjectTaskResultDto> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/open${param.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((responseData: any) => plainToClass(ProjectTaskResultDto, responseData)));
    }

    getClosedProjectTasks(param: ClosedProjectTaskParameterDto): Observable<ProjectTaskResultDto> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/closed${param.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((responseData: any) => plainToClass(ProjectTaskResultDto, responseData)));
    }

    getProjectTaskHistory(projectTaskId: number, clearCache: boolean): Observable<ProjectTaskHistoryResultDto[]> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/history?projectTaskId=${projectTaskId}&clearCache=${clearCache}`;
        this.cacheRegistrationService.registerForCache(url, clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((responseData: any[]) => plainToClass(ProjectTaskHistoryResultDto, responseData)));
    }

    getProjectTaskAttachments(projectTaskId: number, communityId: number): Observable<FileInfoDisplayResultDto[]> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/attachments?projectTaskId=${projectTaskId}&communityId=${communityId}`;
        return this.httpClient
            .get(url)
            .pipe(map((responseData: any[]) => plainToClass(FileInfoDisplayResultDto, responseData)));
    }

    getProjectTaskEditor(param: ProjectTaskEditorParameterDto): Observable<ProjectTaskEditorResultDto> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/editor${param.getQueryString()}`;
        return this.httpClient
            .get(url)
            .pipe(map((responseData: any) => plainToClass(ProjectTaskEditorResultDto, responseData)));
    }

    getEmailReminders(param: EmailReminderParameterDto): Observable<EmailReminderResultDto> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/emailReminders${param.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, param.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((responseData: any) => plainToClass(EmailReminderResultDto, responseData)));
    }

    downloadProjectTaskDocs(param: ProjectTaskAttachmentParameterDto): Observable<Blob> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/download/attachment${param.getQueryString()}`;
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    getLinkedEmailsToProjectTask(param: ProjectTaskLinkTaskParametersDto): Observable<EmailLinkResultDto[]> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/email/linked${param.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, param.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((responseData: any[]) => plainToClass(EmailLinkResultDto, responseData)));
    }

    unlinkEmailsFromProjectTask(param: UnlinkEmailParametersDto): Observable<any> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/email/unlink`;
        return this.httpClient.post(url, classToPlain(param));
    }

    linkEmailsToProjectTask(param: LinkEmailsParametersDto): Observable<any> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/email/link`;
        return this.httpClient.post(url, classToPlain(param));
    }

    getInternalTaskForProjectTask(
        param: ProjectTaskLinkTaskParametersDto
    ): Observable<LinkInternalTaskEditorResultDto> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/internaltask/link/editor${param.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, param.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((responseData: any) => plainToClass(LinkInternalTaskEditorResultDto, responseData)));
    }

    unlinkInternalTaskForProjectTask(param: UnlinkInternalTasksParametersDto): Observable<any> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/internaltask/unlink`;
        return this.httpClient.post(url, classToPlain(param));
    }

    updateProjectTaskStatus(param: ProjectTaskUpdateStatusParameterDto): Observable<any> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/status`;
        return this.httpClient.put(url, classToPlain(param));
    }

    getResponsibleUsers(baseParam: ProjectTaskParameterDto): Observable<ProjectTaskResponsibleUsersResultDto[]> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/responsibleusers${baseParam.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParam.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((responseData: any[]) => plainToClass(ProjectTaskResponsibleUsersResultDto, responseData)));
    }

    createProjectTask(param: AddProjectTaskParameterDto): Observable<any> {
        const url = URLConstants.ccProjectTaskBaseApiUrl;
        return this.httpClient.post(url, classToPlain(param));
    }

    updateProjectTask(param: UpdateProjectTaskParameterDto): Observable<any> {
        const url = URLConstants.ccProjectTaskBaseApiUrl;
        return this.httpClient.put(url, classToPlain(param));
    }

    getNotificationSettings(): Observable<ProjectTaskNotificationSettingsRawDto> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/notification-settings`;
        return this.httpClient
            .get(url)
            .pipe(map((responseData: any) => plainToClass(ProjectTaskNotificationSettingsRawDto, responseData)));
    }

    updateNotificationSetting(param: ProjectTaskNotificationSettingsRawDto): Observable<any> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/notification-settings`;
        return this.httpClient.put(url, classToPlain(param));
    }

    sendReminderMail(param: ProjectTaskReminderEmailParameterDto): Observable<any> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/reminderemail`;
        return this.httpClient.post(url, classToPlain(param));
    }

    uploadProjectTaskDocument(param: ProjectTaskManageAttachmentsParametersDto): Observable<any> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/attachments`;
        return this.httpClient.post(url, classToPlain(param));
    }

    getOpenWorkOrders(baseParams: OpenLinkableWorkOrderParameterDto): Observable<WorkOrderLinkResultDto[]> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/workorders/linkable/open${baseParams.getQueryString()}`;
        return this.httpClient.get(url).pipe(map((response: any[]) => plainToClass(WorkOrderLinkResultDto, response)));
    }

    getClosedWorkOrders(baseParams: ClosedLinkableWorkOrderParameterDto): Observable<WorkOrderLinkResultDto[]> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/workorders/linkable/close${baseParams.getQueryString()}`;
        return this.httpClient.get(url).pipe(map((response: any[]) => plainToClass(WorkOrderLinkResultDto, response)));
    }

    manageWorkOrders(param: ProjectTaskManageWorkOrderParameterDto): Observable<any> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/workorders/manage`;
        return this.httpClient.post(url, classToPlain(param));
    }

    getProjectTaskDetails(baseParams: ProjectTaskViewerParameterDto): Observable<ProjectTaskItemResultDto> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/details${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((responseData: any) => plainToClass(ProjectTaskItemResultDto, responseData)));
    }

    getProjectTaskNotes(id: string, clearCache: boolean): Observable<BaseProcessNotesResultDto> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/notes?id=${id}&clearCache=${clearCache}`;
        this.cacheRegistrationService.registerForCache(url, clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };
        return this.httpClient
            .get(url, options)
            .pipe(map((responseData: any) => plainToClass(BaseProcessNotesResultDto, responseData)));
    }

    addProjectTaskNotes(param: BaseSaveProcessNotesParametersDto): Observable<any> {
        const url = `${URLConstants.ccProjectTaskBaseApiUrl}/notes`;
        return this.httpClient.post(url, classToPlain(param));
    }

    updateProjectTaskEmailReminders(taskId: number, emailReminderInfo: EmailReminderInfo[]): void {
        const node: EmailReminderItemInfo = {
            taskId,
            items: emailReminderInfo
        };
        this._emailRemindersSource.push(node);
    }

    getProjectTaskEmailReminders(taskIdToSearch: number): EmailReminderInfo[] {
        const source = this._emailRemindersSource.find((d) => Utils.equals(d.taskId, taskIdToSearch));
        return Utils.notNullAndDefined(source) &&
            Utils.notNullAndDefined(source.items) &&
            Utils.notNullOrEmpty(source.items)
            ? source.items
            : null;
    }
}
